'use client'

import { cn } from '@repo/ui/lib/utils'
import * as React from 'react'

type TableProps = React.ComponentProps<'table'> & {
  isStriped?: boolean
}

function Table({ className, isStriped = false, style, ...props }: TableProps) {
  // If a specific width is set in style, don't force w-full
  const hasFixedWidth = style?.width !== undefined

  return (
    <table
      className={cn(
        'group/table relative table-fixed caption-bottom text-sm',
        !hasFixedWidth && 'w-full',
        className,
      )}
      data-slot="table"
      data-striped={isStriped || undefined}
      style={style}
      {...props}
    data-sentry-component="Table" data-sentry-source-file="table.tsx" />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      className={cn(
        '[&_tr]:border-b [&_tr:last-child]:border-0 group-data-[striped=true]/table:[&_tr:nth-child(even)]:bg-table-striped group-data-[striped=true]/table:[&_tr:nth-child(even):hover]:bg-table-striped-hover',
        className,
      )}
      data-slot="table-body"
      {...props}
    data-sentry-component="TableBody" data-sentry-source-file="table.tsx" />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      data-slot="table-caption"
      {...props}
    data-sentry-component="TableCaption" data-sentry-source-file="table.tsx" />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      className={cn(
        'p-2 align-middle [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        className,
      )}
      data-slot="table-cell"
      {...props}
    data-sentry-component="TableCell" data-sentry-source-file="table.tsx" />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      data-slot="table-footer"
      {...props}
    data-sentry-component="TableFooter" data-sentry-source-file="table.tsx" />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'min-h-10 px-2 py-2.5 text-left align-middle font-medium text-wrap text-foreground [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        className,
      )}
      data-slot="table-head"
      {...props}
    data-sentry-component="TableHead" data-sentry-source-file="table.tsx" />
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead className={cn(className)} data-slot="table-header" {...props} data-sentry-component="TableHeader" data-sentry-source-file="table.tsx" />
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'bg-background transition-colors hover:bg-muted data-[state=selected]:bg-table-selected! data-[state=selected]:hover:bg-table-selected!',
        className,
      )}
      data-slot="table-row"
      {...props}
    data-sentry-component="TableRow" data-sentry-source-file="table.tsx" />
  )
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow }
